import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRight, Check, Calendar, MapPin, Users, Sparkles, Loader2, CreditCard, FileText, DollarSign } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Map CMS ticket IDs to Stripe package names
const TICKET_TO_STRIPE_MAP = {
  'Standard Pass': 'standard_pass',
  'VIP Pass': 'vip_pass',
  'Virtual Pass': 'virtual_pass'
};

const SignupPage = () => {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    role: '',
    ticketType: '',
    dietaryRequirements: '',
    agreeTerms: false,
    paymentMethod: 'card', // 'card' or 'invoice'
    sponsorshipAmount: 1000
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [registrationId, setRegistrationId] = useState(null);
  const [ticketTypes, setTicketTypes] = useState([]);
  const [isLoadingPasses, setIsLoadingPasses] = useState(true);
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [showSponsorshipOption, setShowSponsorshipOption] = useState(false);

  // Check for cancelled payment
  useEffect(() => {
    if (searchParams.get('cancelled') === 'true') {
      setError('Payment was cancelled. Please try again.');
    }
  }, [searchParams]);

  // Fetch ticket passes from CMS
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch ticket passes
        const passesResponse = await fetch(`${BACKEND_URL}/api/cms/ticket-passes`);
        if (passesResponse.ok) {
          const passes = await passesResponse.json();
          const transformedPasses = passes.map(pass => ({
            id: pass.id,
            name: pass.name,
            price: pass.price_display || `$${pass.price}`,
            priceValue: pass.price,
            features: pass.features || [],
            recommended: pass.is_featured,
            soldOut: pass.is_sold_out,
            badgeColor: pass.badge_color,
            description: pass.description,
            stripeKey: TICKET_TO_STRIPE_MAP[pass.name] || pass.name.toLowerCase().replace(/\s+/g, '_')
          }));
          setTicketTypes(transformedPasses);
          if (transformedPasses.length > 0 && !formData.ticketType) {
            setFormData(prev => ({ ...prev, ticketType: transformedPasses[0].id }));
          }
        }

        // Fetch payment config
        const paymentResponse = await fetch(`${BACKEND_URL}/api/payments/config`);
        if (paymentResponse.ok) {
          const config = await paymentResponse.json();
          setPaymentConfig(config);
        }
      } catch (err) {
        console.error('Failed to fetch data:', err);
        setTicketTypes([
          { id: 'standard', name: 'Standard Pass', price: '$599', priceValue: 599, features: ['Full conference access', 'Networking sessions', 'Conference materials'], stripeKey: 'standard_pass' }
        ]);
        setFormData(prev => ({ ...prev, ticketType: 'standard' }));
      } finally {
        setIsLoadingPasses(false);
      }
    };
    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    const selectedTicket = ticketTypes.find(t => t.id === formData.ticketType);
    if (!selectedTicket && !showSponsorshipOption) {
      setError('Please select a ticket type');
      setIsLoading(false);
      return;
    }

    try {
      const ticketKey = showSponsorshipOption ? 'sponsorship' : selectedTicket.stripeKey;
      
      const response = await fetch(`${BACKEND_URL}/api/payments/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ticket_type: ticketKey,
          origin_url: window.location.origin,
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          company: formData.company || null,
          role: formData.role || null,
          dietary_requirements: formData.dietaryRequirements || null,
          sponsorship_amount: showSponsorshipOption ? formData.sponsorshipAmount : null,
          request_invoice: formData.paymentMethod === 'invoice'
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Registration failed');
      }

      if (data.payment_method === 'invoice') {
        // Invoice requested - show success
        setRegistrationId(data.registration_id);
        setIsSubmitted(true);
      } else if (data.checkout_url) {
        // Redirect to Stripe checkout
        window.location.href = data.checkout_url;
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const selectedTicket = ticketTypes.find(t => t.id === formData.ticketType);
  const displayPrice = showSponsorshipOption 
    ? `$${formData.sponsorshipAmount.toLocaleString()}` 
    : selectedTicket?.price || '$0';

  if (isSubmitted) {
    return (
      <div className="min-h-screen pt-24 pb-12 flex items-center justify-center">
        <div className="max-w-lg mx-auto text-center px-6">
          <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-green-500" />
          </div>
          <h1 className="font-playfair text-3xl font-bold mb-4">
            {formData.paymentMethod === 'invoice' ? 'Invoice Request Submitted!' : 'Registration Complete!'}
          </h1>
          <p className="text-muted-foreground mb-8">
            {formData.paymentMethod === 'invoice' 
              ? 'Your invoice request has been received. You will receive an invoice via email within 2 business days.'
              : 'Thank you for registering for the Quantum Housing Conference. You will receive a confirmation email shortly with your ticket details.'}
          </p>
          {registrationId && (
            <p className="text-sm text-muted-foreground mb-4">
              Registration ID: <span className="font-mono text-primary">{registrationId}</span>
            </p>
          )}
          <div className="glass-card rounded-xl p-6 mb-8 text-left">
            <h3 className="font-semibold mb-4">Event Details</h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-primary" />
                <span>April 17th, 2026 • 12:00 PM EST</span>
              </div>
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-primary" />
                <span>Tsai Auditorium, Harvard University</span>
              </div>
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-primary" />
                <span>{showSponsorshipOption ? 'Partner-/Sponsorship' : selectedTicket?.name}</span>
              </div>
            </div>
          </div>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-8 py-4 bg-primary text-primary-foreground rounded-full font-semibold transition-all hover:-translate-y-1"
          >
            Back to Home
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-12">
      {/* Hero */}
      <section className="px-6 md:px-12 lg:px-24 mb-12">
        <div className="max-w-4xl mx-auto text-center">
          <span className="text-sm uppercase tracking-widest text-secondary font-medium mb-4 block">
            April 17th, 2026
          </span>
          <h1 className="font-playfair text-4xl md:text-6xl font-bold tracking-tight mb-6" data-testid="signup-title">
            Register for the <span className="text-gradient-quantum">Conference</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Secure your spot at the Quantum Housing Conference. Choose your pass 
            and join us at Harvard or virtually from anywhere in the world.
          </p>
        </div>
      </section>

      {/* Ticket Selection */}
      <section className="px-6 md:px-12 lg:px-24 mb-12" data-testid="ticket-selection">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-playfair text-2xl font-semibold text-center mb-8">
            Choose Your Pass
          </h2>
          {isLoadingPasses ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : (
            <>
              <div className="grid md:grid-cols-3 gap-6 mb-6">
                {ticketTypes.map((ticket) => (
                  <div
                    key={ticket.id}
                    className={`glass-card rounded-xl p-6 transition-all ${
                      ticket.soldOut ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:-translate-y-1'
                    } ${
                      !showSponsorshipOption && formData.ticketType === ticket.id 
                        ? 'border-primary border-2' 
                        : 'hover:border-primary/50'
                    } ${ticket.recommended ? 'relative' : ''}`}
                    onClick={() => {
                      if (!ticket.soldOut) {
                        setFormData(prev => ({ ...prev, ticketType: ticket.id, paymentMethod: 'card' }));
                        setShowSponsorshipOption(false);
                      }
                    }}
                    data-testid={`ticket-${ticket.id}`}
                  >
                    {ticket.recommended && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-primary text-primary-foreground text-xs font-medium rounded-full">
                        Recommended
                      </div>
                    )}
                    {ticket.soldOut && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-red-500 text-white text-xs font-medium rounded-full">
                        Sold Out
                      </div>
                    )}
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        !showSponsorshipOption && formData.ticketType === ticket.id 
                          ? 'border-primary bg-primary' 
                          : 'border-muted-foreground'
                      }`}>
                        {!showSponsorshipOption && formData.ticketType === ticket.id && (
                          <Check className="w-3 h-3 text-primary-foreground" />
                        )}
                      </div>
                      <h3 className="font-playfair text-xl font-semibold">{ticket.name}</h3>
                    </div>
                    <p className="text-3xl font-bold mb-6" style={{ color: ticket.badgeColor || 'var(--primary)' }}>{ticket.price}</p>
                    {ticket.description && (
                      <p className="text-sm text-muted-foreground mb-4">{ticket.description}</p>
                    )}
                    <ul className="space-y-2">
                      {ticket.features.map((feature, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <Sparkles className="w-4 h-4 text-secondary mt-0.5 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              {/* Sponsorship Option */}
              <div
                className={`glass-card rounded-xl p-6 transition-all cursor-pointer hover:-translate-y-1 ${
                  showSponsorshipOption ? 'border-primary border-2' : 'hover:border-primary/50'
                }`}
                onClick={() => setShowSponsorshipOption(true)}
                data-testid="sponsorship-option"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    showSponsorshipOption ? 'border-primary bg-primary' : 'border-muted-foreground'
                  }`}>
                    {showSponsorshipOption && <Check className="w-3 h-3 text-primary-foreground" />}
                  </div>
                  <h3 className="font-playfair text-xl font-semibold">Partner-/Sponsorship</h3>
                  <span className="px-2 py-0.5 text-xs rounded bg-secondary/20 text-secondary">Custom Amount</span>
                </div>
                <p className="text-muted-foreground mb-4">Support the conference and gain exclusive benefits. Minimum contribution: $1,000</p>
                {showSponsorshipOption && (
                  <div className="mt-4 p-4 bg-muted/30 rounded-lg">
                    <label className="block text-sm font-medium mb-2">Sponsorship Amount (USD)</label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <input
                        type="number"
                        min="1000"
                        step="100"
                        value={formData.sponsorshipAmount}
                        onChange={(e) => setFormData(prev => ({ ...prev, sponsorshipAmount: parseInt(e.target.value) || 1000 }))}
                        className="w-full pl-10 pr-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none"
                        data-testid="sponsorship-amount"
                      />
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Registration Form */}
      <section className="px-6 md:px-12 lg:px-24" data-testid="registration-form">
        <div className="max-w-2xl mx-auto">
          <div className="glass-card rounded-2xl p-8">
            <h2 className="font-playfair text-2xl font-semibold mb-6">
              Your Information
            </h2>

            {error && (
              <div className="mb-6 p-4 rounded-xl bg-destructive/10 border border-destructive/30 text-destructive">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium mb-2">First Name *</label>
                  <input type="text" id="firstName" name="firstName" value={formData.firstName} onChange={handleChange} required
                    className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition-colors"
                    placeholder="John" data-testid="input-firstName" />
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium mb-2">Last Name *</label>
                  <input type="text" id="lastName" name="lastName" value={formData.lastName} onChange={handleChange} required
                    className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition-colors"
                    placeholder="Doe" data-testid="input-lastName" />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium mb-2">Email Address *</label>
                <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required
                  className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition-colors"
                  placeholder="john@example.com" data-testid="input-email" />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="company" className="block text-sm font-medium mb-2">Company</label>
                  <input type="text" id="company" name="company" value={formData.company} onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition-colors"
                    placeholder="Your Company" data-testid="input-company" />
                </div>
                <div>
                  <label htmlFor="role" className="block text-sm font-medium mb-2">Job Title</label>
                  <input type="text" id="role" name="role" value={formData.role} onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition-colors"
                    placeholder="Your Role" data-testid="input-role" />
                </div>
              </div>

              <div>
                <label htmlFor="dietaryRequirements" className="block text-sm font-medium mb-2">Dietary Requirements</label>
                <input type="text" id="dietaryRequirements" name="dietaryRequirements" value={formData.dietaryRequirements} onChange={handleChange}
                  className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition-colors"
                  placeholder="Vegetarian, Vegan, Allergies, etc." data-testid="input-dietary" />
              </div>

              {/* Payment Method Selection */}
              <div className="p-4 rounded-xl bg-muted/30 border border-border">
                <label className="block text-sm font-medium mb-3">Payment Method</label>
                <div className={`grid ${showSponsorshipOption ? 'md:grid-cols-2' : ''} gap-4`}>
                  <div
                    className={`flex items-center gap-3 p-4 rounded-xl cursor-pointer transition-all ${
                      formData.paymentMethod === 'card' 
                        ? 'bg-primary/10 border-2 border-primary' 
                        : 'bg-background border border-border hover:border-primary/50'
                    }`}
                    onClick={() => setFormData(prev => ({ ...prev, paymentMethod: 'card' }))}
                    data-testid="payment-card"
                  >
                    <CreditCard className={`w-5 h-5 ${formData.paymentMethod === 'card' ? 'text-primary' : 'text-muted-foreground'}`} />
                    <div>
                      <p className="font-medium">Pay by Card</p>
                      <p className="text-xs text-muted-foreground">Secure payment via Stripe</p>
                    </div>
                  </div>
                  {showSponsorshipOption && (
                    <div
                      className={`flex items-center gap-3 p-4 rounded-xl cursor-pointer transition-all ${
                        formData.paymentMethod === 'invoice' 
                          ? 'bg-primary/10 border-2 border-primary' 
                          : 'bg-background border border-border hover:border-primary/50'
                      }`}
                      onClick={() => setFormData(prev => ({ ...prev, paymentMethod: 'invoice' }))}
                      data-testid="payment-invoice"
                    >
                      <FileText className={`w-5 h-5 ${formData.paymentMethod === 'invoice' ? 'text-primary' : 'text-muted-foreground'}`} />
                      <div>
                        <p className="font-medium">Request Invoice</p>
                        <p className="text-xs text-muted-foreground">Pay later via bank transfer</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-start gap-3">
                <input type="checkbox" id="agreeTerms" name="agreeTerms" checked={formData.agreeTerms} onChange={handleChange} required
                  className="mt-1 w-5 h-5 rounded border-border text-primary focus:ring-primary" data-testid="checkbox-terms" />
                <label htmlFor="agreeTerms" className="text-sm text-muted-foreground">
                  I agree to the terms and conditions and consent to receiving communications about the conference and related events.
                </label>
              </div>

              <div className="pt-4">
                <div className="flex items-center justify-between mb-4 p-4 rounded-xl bg-muted/50">
                  <span className="text-muted-foreground">Selected:</span>
                  <span className="font-semibold">
                    {showSponsorshipOption ? 'Partner-/Sponsorship' : selectedTicket?.name} — {displayPrice}
                  </span>
                </div>
                <button type="submit" disabled={isLoading} data-testid="submit-registration"
                  className="w-full flex items-center justify-center gap-2 px-8 py-4 bg-primary text-primary-foreground rounded-full font-semibold text-lg transition-all hover:-translate-y-1 hover:shadow-[0_0_40px_rgba(var(--primary-rgb),0.5)] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0">
                  {isLoading ? (
                    <><Loader2 className="w-5 h-5 animate-spin" /> Processing...</>
                  ) : formData.paymentMethod === 'invoice' ? (
                    <><FileText className="w-5 h-5" /> Request Invoice</>
                  ) : (
                    <><CreditCard className="w-5 h-5" /> Proceed to Payment</>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SignupPage;
